<template>
  <div v-if="loading" class="loading">{{ $tr('common.loading') }}</div>
  <div v-else-if="error" class="error">{{ error }}</div>
  <div v-else class="pub_page">
    <div class="left_top_block">
      <div class="header">
        <div class="left">
          <div class="response-date">{{ responseDate }}</div>
          <div class="name-block">
            <div class="name">{{ full_name }}</div>

            <div class="cv-row" v-if="isCandidateLink">
              <a
                class="cv-row-resume-link"
                v-if="resumeLinkFile"
                :href="resumeLinkFile"
                target="_blank"
                rel="noopener"
                v-tooltip="{
                  content: resumeLinkFile,
                  placement: 'top-center',
                  classes: 'cv-tooltip'
                }"
              >
                <svg class="cv-row-icon">
                  <use xlink:href="/static/icons/cv-icons.svg#file"></use>
                </svg>
              </a>
              <a
                class="cv-row-resume-link"
                v-if="resumeLinkUrl"
                :href="resumeLinkUrl"
                target="_blank"
                rel="noopener"
                v-tooltip="{
                  content: resumeLinkUrl,
                  placement: 'top-center',
                  classes: 'cv-tooltip'
                }"
              >
                <svg class="cv-row-icon">
                  <use xlink:href="/static/icons/cv-icons.svg#url"></use>
                </svg>
              </a>
            </div>
          </div>

          <div class="contacts_block">
            <div class="vacancy_name">{{ response.vacancy.name }}</div>
            <div class="info_block" v-if="isCandidateLink">
              <a class="email_color" :href="'mailto:' + candidate.email">{{
                candidate.email
              }}</a>
              <span>{{ candidate.phone }}</span>
            </div>
          </div>
        </div>
        <div class="right">
          <a :href="langingUrl" target="_blank" rel="noopener">
            <img src="/static/images/logo.svg" class="vlogo" />
          </a>
        </div>
      </div>
      <div class="vcontent">
        <div ref="vblock" style="position: relative;" v-if="has_answers">
          <div
            v-for="(question, index) in response.questions"
            :key="question.id"
            @contextmenu.prevent
          >
            <vrecursive-block
              class="recursive-block"
              v-if="!is_video_started && !first_click_was"
              :count="response.questions.length - 1"
              :current_index="index"
              :handle_click="first_play_click"
            >
              <div class="play_first">play</div>
            </vrecursive-block>
            <player
              :class="{ vphide: num !== index }"
              v-if="!is_not_video(question)"
              :ref="'player_' + index"
              :src="question.video ? question.video.source : ''"
              :muted="config.muted"
              :rate="config.rate"
              :index="index"
              :volume="config.volume"
              :height="452"
              class="response-player is-mirror"
              @inited="set_controls"
              @changed="save_config"
              @ended="next_answer"
              @play="set_answer_state(index, true)"
              @paused="set_answer_state(index, false)"
            />

            <div v-else :class="{ vphide: num !== index }">
              <div class="dummy">
                <img src="/static/images/elements/dummy-video.svg" />
                <div class="dummy-description">
                  {{ get_dummy_video_description(question) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex-wrapper">
          <emotions
            class="flex-wrapper_left"
            v-if="isEmotionsVisible"
            :video="currentItem.video"
          />
          <vpaginator
            class="flex-wrapper_right"
            v-if="has_video_answers"
            :first="num === first_num_with_video"
            :last="num === last_num_with_video"
            :show_prev_btn="show_prev_btn"
            :show_next_btn="show_next_btn"
            @next="next_answer"
            @prev="prev_answer"
          />
        </div>
      </div>
    </div>
    <div class="right_bottom_block">
      <div class="header">
        <div class="top-tabs">
          <ul class="submenu">
            <li
              v-for="tab in tabs"
              :key="tab"
              :class="['submenu-item', { active: tab === activeTab }]"
            >
              <a @click="changeTab(tab)">
                {{ $tr(`responses.tabs.${tab}.name`) }}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div class="vcontent">
        <tab-application
          v-if="activeTab === 'application'"
          :records="tabApplicationList"
        />
        <tab-answers
          v-if="activeTab === 'answers'"
          :questions="response.questions"
          :responseId="response.id"
          @set_active_answer="set_active_answer"
        />
        <tab-competences
          v-if="activeTab === 'competences'"
          :responseId="response.id"
          :competences="competences"
          :overallRating="overallRating"
        />
        <tab-comments
          v-if="activeTab === 'comments'"
          :comments="response.comments"
          :responseId="response.id"
          :canAdd="false"
        />
        <tab-test
          v-if="activeTab === 'test'"
          :responseId="response.id"
          :response="response"
          :vacancyToken="vacancyToken"
          :test="test"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import Axios from 'axios';
  import moment from 'moment';
  import { mapState } from 'vuex';
  import Errors from '@/api/errors.json';

  import TabAnswers from '@/components/response/tabs/TabAnswersSafari.vue';
  import TabCompetences from '@/components/publicResponse/TabCompetences.vue';
  import TabTest from '@/components/publicResponse/TabTest.vue';
  import TabApplication from '@/components/response/tabs/tabApplication.vue';
  import TabComments from '@/components/response/tabs/TabComments.vue';
  import player from '@/routes_new/inner/response/components/player.vue';
  import SafariBrowserDetector from '@/components/mixin/SafariBrowserDetector.js';
  import vpaginator from '@/routes_new/inner/response/components/video_paginator.vue';
  import Emotions from '@/components/response/videointerview/Emotions.vue';

  export default {
    name: 'default',
    props: ['responseToken'],
    data() {
      return {
        response: null,
        error: null,
        activeTab: 'application',
        loading: true,
        competences: null,
        test: null,
        overallRating: null,
        refresh: true,
        controls: [],
        num: 0,
        tabChanged: false,
        catched: false,
        top_poistion: null,
        playclick_count: 0,
        config: {
          muted: false,
          rate: 1,
          volume: 1
        }
      };
    },
    components: {
      TabAnswers,
      TabCompetences,
      TabComments,
      TabApplication,
      TabTest,
      player,
      vpaginator,
      Emotions
    },
    mixins: [SafariBrowserDetector],
    computed: {
      ...mapState(['locale']),
      first_num_with_video() {
        let result = 0;
        let list = this.response.questions.map(i => i.video);
        for (let i = 0; i < list.length; i++) {
          if (list[i] !== null) {
            result = i;
            break;
          }
        }
        return result;
      },
      last_num_with_video() {
        let result = 0;
        let list = this.response.questions.map(i => i.video);
        for (let i = list.length - 1; i >= 0; i--) {
          if (list[i] !== null) {
            result = i;
            break;
          }
        }
        return result;
      },
      dateFormat() {
        return this.is_japan_locale ? 'YYYY.MM.DD' : 'DD.MM.YYYY';
      },
      resumeLinkFile() {
        return this.response ? this.response.resume_url : null;
      },

      resumeLinkUrl() {
        return this.response ? this.response.second_resume_url : null;
      },
      responseDate() {
        if (!this.response || !this.response.date_sent) {
          return '';
        }
        const day = moment(this.response.date_sent, 'YYYY-MM-DDThh:mm');

        if (!day) {
          return '';
        }

        return day.format(`${this.dateFormat} HH:mm`);
      },
      companyLang() {
        if (
          !this.response ||
          !this.response.company ||
          !this.response.company.language
        ) {
          return null;
        }

        return this.response.company.language.code;
      },
      isCandidateLink() {
        return this.response && this.response.is_candidate_link;
      },
      langingUrl() {
        let lang = '';
        if (this.companyLang) {
          lang = this.companyLang;
        } else {
          lang = this.locale ? this.locale.value : 'en';
        }

        switch (lang) {
          case 'ru':
            return 'https://vcv.ru';
          case 'ja':
            return 'https://vcv.co.jp';
          default:
            return 'https://vcv.ai';
        }
      },
      questions() {
        if (!this.response) {
          return [];
        }

        return this.response.questions;
      },
      selected_question() {
        if (!this.questions || !this.questions.length) {
          return null;
        }

        return this.questions[this.num];
      },
      tabApplicationList() {
        if (!this.response) {
          return [];
        }
        return this.response.survey;
      },
      currentItem() {
        if (!this.questions || !this.questions.length) {
          return null;
        }
        return this.questions[this.questionIndex]
          ? this.questions[this.questionIndex]
          : null;
      },
      isEmotionsVisible() {
        return (
          this.currentItem &&
          this.currentItem.video &&
          this.currentItem.video.emotions &&
          this.currentItem.video.emotions.length !== 0
        );
      },
      has_answers() {
        return this.response.questions.length > 0;
      },
      has_video_answers() {
        if (!this.response || !this.response.questions.length) {
          return false;
        }

        let has_video = false;
        this.questions.forEach(question => {
          if (question.video !== null) {
            has_video = true;
          }
        });
        return has_video;
      },
      show_prev_btn() {
        if (
          !this.response ||
          !this.response.questions ||
          !this.response.questions.length
        ) {
          return false;
        }
        let has_video = false;

        for (let i = this.num; i >= 0; i--) {
          if (this.response.questions[i].video !== null) {
            has_video = true;
          }
        }

        return has_video;
      },
      show_next_btn() {
        if (
          !this.response ||
          !this.response.questions ||
          !this.response.questions.length
        ) {
          return false;
        }
        let has_video = false;

        for (let i = this.num; i <= this.response.questions.length - 1; i++) {
          if (this.response.questions[i].video !== null) {
            has_video = true;
          }
        }

        return has_video;
      },
      is_playing() {
        return this.$store.getters['vacancy/videointerview/is_playing'];
      },
      candidate() {
        return this.response ? this.response.candidate : null;
      },
      candidateName() {
        if (this.candidate) {
          return `${this.candidate.name}`;
        } else {
          return '';
        }
      },
      candidateSurName() {
        if (this.candidate && typeof this.candidate.surname === 'string') {
          return this.isCandidateLink
            ? this.candidate.surname
            : `${this.candidate.surname.charAt(0)}.`;
        } else {
          return '';
        }
      },
      full_name() {
        if (!this.is_japan_locale) {
          return `${this.candidateName} ${
            this.isCandidateLink
              ? this.candidateSurName
              : this.candidateSurName.charAt(0) + '.'
          }`;
        } else {
          /* TODO: Show full name and surname */
          return `${this.candidate.surname} ${this.candidate.name}`;
        }
      },
      lang() {
        if (
          !this.response ||
          !this.response.vacancy ||
          !this.response.vacancy.language
        ) {
          return '';
        }

        return this.response.vacancy.language.code;
      },
      vacancyToken() {
        if (
          !this.response ||
          !this.response.vacancy ||
          !this.response.vacancy.token
        ) {
          return '';
        }

        return this.response.vacancy.token;
      },
      is_japan_locale() {
        return this.lang === 'ja';
      },
      tabs() {
        const tabs = [];
        if (
          this.response &&
          this.response.survey &&
          this.response.survey.length > 0
        ) {
          tabs.push('application');
        }
        if (
          this.response &&
          this.response.questions &&
          this.response.questions.length > 0
        ) {
          tabs.push('answers');
        }
        if (this.competences && this.competences.length) {
          tabs.push('competences');
        }
        if (this.test) {
          tabs.push('test');
        }
        // tabs.push('comments')
        return tabs;
      },
      is_video_started() {
        return this.$store.getters['interview/videointerview/is_video_started'];
      },
      first_click_was() {
        return this.$store.getters['interview/videointerview/first_click_was'];
      },
      questionIndex() {
        return this.$store.getters['vacancy/videointerview/index'];
      }
    },
    watch: {
      response(p, n) {
        if (!!p.id) {
          if (!!p.candidate) {
            document.title = `${this.full_name} - ${this.response.vacancy.name}`;
          }
        }
      },
      questionIndex(val) {
        this.num = val;
      },
      tabs: {
        immediate: true,
        handler(val) {
          if (val && val.length && !this.tabChanged) {
            this.activeTab = val[0];
          }
        }
      }
    },
    methods: {
      save_config(data) {
        // volume, muted, rate
        this.config.volume = data.volume;
        this.config.muted = data.muted;
        this.config.rate = data.rate;
      },
      changeTab(tab) {
        this.activeTab = tab;
        this.tabChanged = true;
      },
      is_not_video(question) {
        return (
          question.is_blocked ||
          question.is_video_error ||
          question.is_question_skipped
        );
      },
      get_dummy_video_description(question) {
        return this.$tr('videoInterview.question.is_video_error');
      },
      first_play_click(val, current_index) {
        if (this.questions[val].video === null) {
          return;
        }

        if (this.is_video_started || this.first_click_was) {
          return;
        }
        // На iOs при первом клике на вопрос выполянется мгновенный play, затем pause и unmute всех видео вопросов.
        // Сделано, чтобы на iOs корректно отрабатывало автопереключение видео
        // if (this.isIos || this.isSafari) {

        let result = this.controls.find(control => control.index === val);
        if (result) {
          result.play();
        }

        if (this.num !== val) {
          if (result) {
            result.pause();
          }
        }
        this.playclick_count = this.playclick_count + 1;
        if (result !== null) {
          if (this.playclick_count === this.response.questions.length - 1) {
            this.$store.commit(
              'interview/videointerview/set_first_click_was',
              true
            );
          }
          if (this.playclick_count >= this.response.questions.length) {
            this.$store.commit(
              'interview/videointerview/toggle_start_video',
              true
            );
            this.$store.commit('vacancy/videointerview/setIndex', this.num);
          }
        }
      },
      next_answer() {
        const prev_control = this.controls.find(
          control => control.index === this.num
        );
        if (prev_control) {
          prev_control.pause();
        }

        let has_next_video = false;

        for (
          let i = this.num + 1;
          i <= this.response.questions.length - 1;
          i++
        ) {
          if (this.response.questions[i].video !== null) {
            has_next_video = true;
          }
        }

        if (!has_next_video) {
          return;
        }

        if (this.num < this.response.questions.length - 1) {
          let result = this.controls.find(
            control => control.index === this.num + 1
          );
          if (result) {
            result.play(0); // time start 0
          } else {
            this.num++;
            return this.next_answer();
          }
          let has_video = false;
          for (let i = this.num + 1; i < this.response.questions.length; i++) {
            if (this.response.questions[i].video !== null) {
              has_video = true;
            }
          }
          if (has_video) {
            this.num++;
            if (result === null) {
              this.next_answer();
              return;
            }
            this.$store.commit('vacancy/videointerview/setIndex', this.num);
          }
        } else {
          this.$store.commit('vacancy/videointerview/set_playing', false);
        }
      },
      prev_answer() {
        const prev_control = this.controls.find(
          control => control.index === this.num
        );
        if (prev_control) {
          prev_control.pause();
        }
        if (this.num > 0) {
          this.num--;
          let result = this.controls.find(
            control => control.index === this.num
          );
          if (result) {
            result.play(0); // time start 0
          } else {
            return this.prev_answer();
          }
          this.$store.commit('vacancy/videointerview/setIndex', this.num);
        } else {
          this.$store.commit('vacancy/videointerview/set_playing', false);
        }
      },
      set_active_answer(num) {
        let time;
        let control;
        if (this.num !== num) {
          control = this.controls.find(control => control.index === this.num);
          if (control) {
            control.pause();
          }
          time = 0;
        }
        this.num = num;
        control = this.controls.find(control => control.index === this.num);
        if (control) {
          control.play_pause(time);
        }
      },
      set_controls(controls) {
        const index = controls.index;
        if (this.questions[index].video !== null) {
          this.controls.push(controls);
        }
      },
      set_answer_state(index, played) {
        if (this.num === index) {
          this.$store.commit('vacancy/videointerview/set_playing', played);
        }
      },
      vrdy() {
        var player_el, parent_el;
        if (
          window.innerWidth < 800 &&
          (window.orientation === 0 || window.orientation === undefined)
        ) {
          window.addEventListener('scroll', e => {
            if (window.orientation !== 90) {
              if (this.catched === false && this.top_poistion === null) {
                player_el = this.$refs.vblock;
                parent_el = player_el.parentElement;
                this.top_poistion = this.get_top(player_el);
              }
              if (
                window.scrollY >= this.top_poistion &&
                this.catched === false
              ) {
                parent_el.style.paddingTop = player_el.clientHeight + 20 + 'px';
                this.catched = true;
                player_el.style.position = 'fixed';
                player_el.style.zIndex = '99';
                player_el.style.left = '0';
                player_el.style.right = '0';
                player_el.style.top = '0';
              }
              if (window.scrollY < this.top_poistion && this.catched) {
                this.catched = false;
                player_el.style.position = 'relative';
                parent_el.style.paddingTop = 0 + 'px';
              }
            }
          });
          window.addEventListener('orientationchange', e => {
            if (window.orientation === 90) {
              this.catched = false;
              player_el.style.position = 'relative';
              parent_el.style.paddingTop = 0 + 'px';
            }
          });
        }
      },
      get_top(o) {
        var y = 0;
        while (o !== null) {
          y = y + o.offsetTop;
          o = o.offsetParent;
        }
        return y;
      }
    },
    mounted() {
      this.vrdy();
      Axios.get('/1.0/public-responses/' + this.responseToken)
        .then(response => {
          this.response = response.data.data[0];
          for (let i = 0; i < this.response.questions.length; i++) {
            if (this.response.questions[i].video !== null) {
              this.num = i;
              break;
            }
          }
          this.$store.commit('vacancy/videointerview/setIndex', this.num);
          this.$store.commit('response/setPublicResponse', this.response);
        })
        .then(() => {
          if (
            this.response &&
            this.response.test &&
            this.response.test.result !== null
          ) {
            Axios.get(`/1.0/public-responses/${this.responseToken}/test`).then(
              response => {
                const test =
                  response && response.data && response.data.data
                    ? response.data.data[0]
                    : null;

                if (test) {
                  this.test = test;
                  if (this.tabs.length === 1 && this.tabs[0] === 'test') {
                    this.activeTab = 'test';
                  }
                }
              }
            );
          }
        })
        .then(() =>
          Axios.get(`/1.0/public-responses/${this.responseToken}/competences`)
        )
        .then(response => {
          const answer =
            response &&
            response.data &&
            response.data.data &&
            response.data.data.length > 0
              ? response.data.data[0]
              : null;
          this.competences = answer ? answer.competences : null;
          this.overallRating = answer ? answer.overall_rating : null;
          this.loading = false;
          this.$emit('loaded');
        })
        .catch(err => {
          this.loading = false;
          this.error =
            handleError(err).message !== 'NETWORK_ERROR'
              ? handleError(err).message
              : this.$tr('common.error.networkError');
          this.$emit('loaded');
        });
    }
  };

  function handleError(error) {
    const response = error.response;

    if (error.response) {
      if (!response.data || !response.data.meta || !response.data.meta.error) {
        return Errors['SERVER_ERROR'];
      }

      let error = response.data.meta.error;
      error.id = response.data.meta.id;
      error.status = response.status;

      return error;
    } else if (error.request) {
      return { message: 'NETWORK_ERROR' };
    } else {
      return { message: Errors['UNKNOWN_ERROR'] };
    }
  }
</script>

<style src="./default.scss" lang="scss" scoped></style>
<style src="./default_media.scss" lang="scss" scoped></style>
